import React, { useState } from "react";
import {createNewUserValidation} from "../../../../validations/createNewUserValidation";

import { isBlank } from "../../../../utils";
import { useReSendVerificationEmailMutation } from "../../../../services/accountsApi";

import { STATES } from "../../../../constants";
import { Button, Checkbox, Box, Modal, FormHelperText } from "@mui/material";

import { useSelector } from 'react-redux';
import { UserType } from "../../../../constants/enums";
import { discordSchema } from "../../../../validations/discordValidation";
import { paymentsEmailSchema } from "../../../../validations/paymentsEmailValidation";
import { useCreateAccountMutation } from "../../../../services/accountsApi";
import { useNotification } from '../../../../context/NotificationContext';
import useUserTypes from "../../../../customHooks/useUserTypes";

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 1025,
  boxShadow: 24,
  borderRadius: "32px",
  p: 4,
  backgroundColor: "var(--background-lightgray)",
};

const ERR_MSG = {
  psw_required: "The Password is requrired",
  psw_min_length: "Password min length is six",
  psw_are_equals: "Password are not equals",
};

export default function CreateUser({
  modalIsOpen,
  closeModal,
}) {

  const { showNotification } = useNotification();

  const userTypes = useUserTypes();
  const [ createUserMutation ] = useCreateAccountMutation();

  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    screenName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    mobilePhone: "",
    status: "Verified",
    userTypeId: UserType.NewBee, //A: default userType
    role: "User", //A: default role
    userDistribution: false,
    doNotDroneBuy: false,
    useSwarm: false,
    discordTag: "",
    paymentsEmail: "",
    password: "",
    confirmPassword: "",
  });
  const [isFetching, setIsFetching] = useState(false);
  const [errors, setErrors] = useState([]);
  const [formErrors, setFormErrors] = useState([]);

  function errorMessageExists(errorMsg) {
    return errors.find((err) => {
      if (err.includes(errorMsg)) {
        return true;
      }
    });
  }

  function cleanErrorMessage(errMsgKey) {
    let errorMsgValue = ERR_MSG[errMsgKey];
    var array = [...errors]; // make a separate copy of the array
    var index = array.indexOf(errorMsgValue);
    if (index !== -1) {
      array.splice(index, 1);
      setErrors(array);
    }
  }
  function cleanAllErrorsMessages() {
    setErrors([]);
  }

  function addErrorMessage(key) {
    let str = ERR_MSG[key];
    if (errorMessageExists(str)) return;
    setErrors((prevErrors) => [...prevErrors, str]);
  }

  function addErrorMessageFromBackEnd(str) {
    setErrors((prevErrors) => [...prevErrors, str]);
  }

  function validatePasswordsAreEquals(password, confirmPassword) {
    if (password !== confirmPassword) {
      addErrorMessage("psw_are_equals");
      return false;
    }
    cleanErrorMessage("psw_are_equals");
    return true;
  }

  function validatePasswordMinLength(password, confirmPassword) {
    if (isBlank(password) && isBlank(confirmPassword)) return true;

    let hasMinLength = password.length >= 6;
    if (!hasMinLength) addErrorMessage("psw_min_length");
    if (hasMinLength) {
      cleanErrorMessage("psw_min_length");
    }
    return hasMinLength;
  }

  function passwordExist(password) {
    if (password == null || isBlank(password)) {
      addErrorMessage("psw_required");
      return false;
    } else {
      cleanErrorMessage(ERR_MSG["psw_required"]);
      return true;
    }
  }

  function validatePassword(passwordIsRequired) {
    let password_clean = (user.password || "").trim();
    let confirmPassword_clean = (user.confirmPassword || "").trim();

    if (passwordIsRequired && !passwordExist(password_clean)) {
      return false;
    }
    const passwordAreEquals = validatePasswordsAreEquals(
      password_clean,
      confirmPassword_clean
    );
    const confirmMinLength = validatePasswordMinLength(
      password_clean,
      confirmPassword_clean
    );
    return passwordAreEquals && confirmMinLength;
  }

  async function validateDiscord() {
    // allow empty discord tag
    if(user.discordTag.length === 0) return true

    const isValid = await discordSchema.isValid({ discordUserName: user.discordTag });
    console.log(`validateDiscord: isValid = ${isValid}`);

    if (!isValid) {
      const errorMessage = "Discord User Name is invalid";
      console.log(`validateDiscord: errorMessage = ${errorMessage}`);
      setErrors((prevErrors) => [...prevErrors, errorMessage]);
    }

    return isValid;
  }

  async function validatePaypalEmail(){
    if (user.email && user.email !== "") {
      let emailIsValid = await paymentsEmailSchema.isValid(user.paymentsEmail);
      if (!emailIsValid) {
        setErrors((prevErrors) => [...prevErrors, "PayPal Email is invalid"]);
      }
      return emailIsValid;
    }
  }

  async function initNewUserProcess() {
    setIsFetching(true);
    cleanAllErrorsMessages();
    createUserMutation(user)
      .then((response) => {
        console.log("User created successfully", response);
        setIsFetching(false);
        showNotification('User created successfully', 'success');
        setUser({});
        closeModal();
      })
      .catch((error) => {
        console.log("Error creating user", error);
        showNotification('Error creating user', 'error');
      })
      .finally(() => {
        setIsFetching(false);
      });
  }

  async function onLocalSubmit() {
    cleanAllErrorsMessages();
    setFormErrors([]);
    console.log("onLocalSubmit user", user);
    try{
      await createNewUserValidation.validate(user, { abortEarly: false });
    }catch(err){
      err.inner.forEach((error) => {
        setFormErrors((prevErrors) => ({ ...prevErrors, [error.path]: error.message }));
      });
      return;
    }
    if( !(await validateDiscord())) return
    if( !(await validatePaypalEmail())) return
    if( !validatePassword(true)) return
    initNewUserProcess();
  }

  function handleRoleChange() {
    user.role === "Admin" ? setUser({ ...user, role: "User" }) : setUser({ ...user, role: "Admin" });
  }

  function handleUserTypeChange(event) {
    setUser({ ...user, userTypeId: event.target.value });

    if (Number(event.target.value) === UserType.HiveAdmin) {
      setUser({ ...user, role: "Admin", userDistribution: false, useSwarm: false, userTypeId: event.target.value });
    }
  }

  function handleUserChange(event){
    const { name, value } = event.target;
    setUser({ ...user, [name]: value });
  }

  return (
    <>
    <Modal
      open={modalIsOpen}
      onClose={closeModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <div>
          <div className="title-id">
            <h4 style={{ fontWeight: "600", color: "var(--orange)", margin: "0" }}> Create Account </h4>
            {/* <p style={{ font: "var(--p2)", margin: "0" }}>Account ID: -</p> */}
          </div>

          <div className="row-modal">
            <div className="column-modal">
              <div className="input-column">
                <label>First Name</label>
                <input
                  label="First Name"
                  name="firstName"
                  value={user.firstName}
                  onChange={handleUserChange}
                />
                <FormHelperText style={{ color: "red" }}>{formErrors["firstName"]} </FormHelperText>
              </div>

              <div className="input-column">
                <label>Last Name</label>
                <input
                  label="Last Name"
                  name="lastName"
                  value={user.lastName}
                  onChange={handleUserChange}
                />
                <FormHelperText style={{ color: "red" }}>{formErrors["lastName"]} </FormHelperText>
              </div>

              <div className="input-column">
                <label>Email</label>
                <input
                  label="Email"
                  name="email"
                  value={user.email}
                  onChange={handleUserChange}
                />
                <FormHelperText style={{ color: "red" }}>{formErrors["email"]} </FormHelperText>
              </div>

              <div className="input-column">
                <label>Screen Name</label>
                <input
                  label="Screen Name"
                  name="screenName"
                  value={user.screenName}
                  onChange={handleUserChange}
                />
                <FormHelperText style={{ color: "red" }}>{formErrors["screenName"]} </FormHelperText>
              </div>

              <div className="input-column">
                <label>Discord User Name</label>
                <input
                  label="Discord User Name"
                  name="discordTag"
                  value={user.discordTag}
                  onChange={handleUserChange}
                />
                <FormHelperText style={{ color: "red" }}>{formErrors["discordTag"]} </FormHelperText>
              </div>

            </div>
            {/* ------------------------------ */}

            <div className="column-modal">
              <div className="input-column">
                <label>Password</label>
                <input
                  type="password"
                  label="Password"
                  name="password"
                  value={user.password}
                  onChange={handleUserChange}
                />
                <FormHelperText style={{ color: "red" }}>{formErrors["password"]} </FormHelperText>
              </div>

              <div className="input-column">
                <label>Confirm Password</label>
                <input
                  type="password"
                  label="Confirm Password"
                  name="confirmPassword"
                  value={user.confirmPassword}
                  onChange={handleUserChange}
                />
                <FormHelperText style={{ color: "red" }}>{formErrors["confirmPassword"]} </FormHelperText>
              </div>

              <div className="input-column">
                <label>Address 1</label>
                <input
                  label="Address 1"
                  name="address1"
                  value={user.address1}
                  onChange={handleUserChange}
                />
                <FormHelperText style={{ color: "red" }}>{formErrors["address1"]} </FormHelperText>
              </div>

              <div className="input-column">
                <label>Address 2</label>
                <input
                  label="Address 2"
                  name="address2"
                  value={user.address2}
                  onChange={handleUserChange}
                />
                <FormHelperText style={{ color: "red" }}>{formErrors["address2"]} </FormHelperText>

              </div>

              <div className="input-column">
                <Box sx={{display: 'flex'}}>
                  <label>PayPal Email</label>
                </Box>
                <input
                  label="PayPal Email"
                  name="paypalEmail"
                  value={user.paymentsEmail}
                  onChange={handleUserChange}
                />
                <FormHelperText style={{ color: "red" }}>{formErrors["paymentsEmail"]} </FormHelperText>
              </div>

            </div>
            {/* ------------------------------ */}
            <div className="column-modal">
              <div className="input-column">
                <label>City</label>
                <input
                  label="City"
                  name="city"
                  value={user.city}
                  onChange={handleUserChange}
                />
                <FormHelperText style={{ color: "red" }}>{formErrors["city"]} </FormHelperText>
              </div>

              <div className="input-column">
                <label>State</label>
                <select
                  label="State"
                  name="state"
                  value={user.state || "default" }
                  onChange={handleUserChange}
                >
                  {!user.state && (
                    <option value="default">
                      {" "}
                      -- select an option --{" "}
                    </option>
                  )}
                  {STATES.map((oneState) => (
                    <option key={oneState.name} value={oneState.name}>{oneState.name}</option>
                  ))}
                </select>
                <FormHelperText style={{ color: "red" }}>{formErrors["state"]} </FormHelperText>
              </div>

              <div className="input-column">
                <label>ZIP</label>
                <input
                  label="ZIP"
                  name="zip"
                  value={user.zip}
                  onChange={handleUserChange}
                />
                <FormHelperText style={{ color: "red" }}>{formErrors["zip"]} </FormHelperText>
              </div>

              <div className="input-column">
                <label>Mobile Phone</label>
                <input
                  label="Mobile Phone"
                  name="mobilePhone"
                  value={user.mobilePhone}
                  onChange={handleUserChange}
                />
                <FormHelperText style={{ color: "red" }}>{formErrors["mobilePhone"]} </FormHelperText>
              </div>

            </div>        

            <div
              className="column-modal"
              style={{ width: "fit-content", margin: "0" }}
            >
              <div className="input-column">
                <label>
                  <h4 style={{ color: "var(--orange)" }}>Status Account</h4>
                </label>
                <select
                  label="State"
                  name="status"
                  value={user.status || "Verified"}
                  onChange={handleUserChange}
                >
                  <option value="Unverified">Unverified</option>
                  <option value="Verified">
                    Verified
                  </option>
                  <option value="Active">Active</option>
                </select>
              </div>

              <div className="input-column">
                <label>
                  <h4 style={{ color: "var(--orange)" }}> User Type</h4>
                </label>
                <select
                  value={user.userTypeId}
                  onChange={handleUserTypeChange}
                >
                  {
                    userTypes?.length > 0 ?
                      userTypes.map((type) => {
                        return (
                          <option
                            key={type.id}
                            value={type.id}
                          >
                            {type.name}
                          </option>
                        );
                      })
                    :
                      <option>Loading...</option>
                  }
                </select>
              </div>

              <div className="checkbox-200px" style={{ margin: "0" }}>
                <Checkbox
                  checked={user.userDistribution}
                  onChange={() => setUser({ ...user, userDistribution: !user.userDistribution })}
                  id="userDistribution"
                  name="User is eligible for distribution"
                />
                <label htmlFor="userDistribution">
                  <p>User is eligible for distribution</p>
                </label>
              </div>

              <div className="checkbox-200px" style={{ margin: "0" }}>
                <Checkbox
                  checked={user.useSwarm}
                  onChange={() => setUser({ ...user, useSwarm: !user.useSwarm })}
                  id="useSwarm"
                  name="User is eligible for Swarms"
                />
                <label htmlFor="useSwarm">
                  <p>User is eligible for Swarms</p>
                </label>
              </div>

              <div className="checkbox-200px" style={{ margin: "0" }}>
                <Checkbox
                  checked={user.doNotDroneBuy}
                  onChange={() => setUser({ ...user, doNotDroneBuy: !user.doNotDroneBuy })}
                  id="doNotDroneBuy"
                  name="User not is eligible for Drone Buy"
                />
                <label htmlFor="doNotDroneBuy">
                  <p>Do Not Drone Buy</p>
                </label>
              </div>

              <div className="checkbox-200px">
                <Checkbox
                  id="systemAdmin"
                  name="System Admin"
                  checked={user.role === "Admin"}
                  onChange={handleRoleChange}
                />
                <label htmlFor="systemAdmin">
                  <p>System Admin</p>
                </label>
              </div>

            </div>
        
          </div>

          {/* ------------------FORM ERRORS------------------------------------- */}

                  <ul style={{ margin: "0 0 0.5em 0", flexDirection: "row", color: "var(--red)" }}>
              {errors.map((oneError, i) => (
                <li key={i} >{oneError}</li>
              ))}
            </ul>      


          {/* ------------------------------------------------------------------ */}

          <div
            className="modal-buttons"
            style={{ justifyContent: "space-between" }}
          >
            <div style={{ display:'flex', justifyContent:'flex-end', width:'100%' }} >
              <Button
                variant="contained"
                color="secondary"
                style={{ margin: "0 2em 0 0", width: "100px" }}
                onClick={() => closeModal()}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                style={{ width: "100px" }}
                onClick={onLocalSubmit}
                disabled={isFetching}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
    </>
  );
}
